import React, { useState } from "react";
import { Box, TextField, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchToggle from "./SearchToggle";
import SendButton from "./SendButton";
import AttachmentChip from "./AttachmentChip";
import { styles } from "./styles";
import { Source } from "../../types/frontend";

/**
 * ChatInputProps interface defines the props for the ChatInput component.
 */
interface ChatInputProps {
  /** The input message string. */
  input: string;
  /** Function to set the input message string. */
  setInput: (query: string) => void;
  /** Indicates if the chat message is currently being processed. */
  isLoading: boolean;
  /** Function to handle sending the message. */
  handleSendMessage: () => void;
  /** Function to handle stopping the generation. */
  handleStopGeneration: () => void;
  /** Function to handle toggling the search. */
  onToggleSearch: () => void;
  /** Indicates if the search is currently active. */
  isSearchOn: boolean;
  /** Styles to apply to the component. */
  styles: any;
  /** The sources attached to the chat. */
  attachedSources: Source[];
  /** Function to remove a source from the chat. */
  onRemoveSource: (sourceId: string) => void;
  /** Whether to hide the search toggle (for v2 variant) */
  hideSearchToggle?: boolean;
}

/**
 * ChatInput Component
 *
 * A component that provides a text input for composing chat messages and a send button.
 *
 * @param {ChatInputProps} props - Component props
 * @returns {JSX.Element} Rendered ChatInput component
 */
const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  isLoading,
  handleSendMessage,
  handleStopGeneration,
  onToggleSearch,
  isSearchOn,
  attachedSources,
  onRemoveSource,
  hideSearchToggle = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();

  return (
    <Box sx={styles.inputContainer(theme)}>
      <Box sx={{ position: "relative", width: "100%" }}>
        {/* Source chips display */}
        {attachedSources.length > 0 && (
          <Box sx={styles.attachmentChipContainer(theme)}>
            {/* Reverse the sources array so newly attached sources appear first */}
            {[...attachedSources].reverse().map((src) => (
              <AttachmentChip
                key={src.id}
                source={src}
                deletable={true}
                onDelete={onRemoveSource}
                sx={{
                  pointerEvents: "auto",
                }}
              />
            ))}
          </Box>
        )}
        {/* Text input */}
        <TextField
          fullWidth
          multiline
          maxRows={8}
          placeholder="Ask anything…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isLoading) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          onFocus={() => setIsFocused(true)}
          autoFocus={true}
          InputProps={{
            startAdornment: !hideSearchToggle ? (
              <InputAdornment position="start">
                <SearchToggle
                  isSearchOn={isSearchOn}
                  onToggleSearch={onToggleSearch}
                  styles={styles}
                  disabled={attachedSources.length > 0}
                />
              </InputAdornment>
            ) : undefined,
            endAdornment: (
              <InputAdornment position="end">
                <SendButton
                  isLoading={isLoading}
                  isFocused={isFocused}
                  hasInput={input.trim().length > 0}
                  handleSendMessage={handleSendMessage}
                  handleStopGeneration={handleStopGeneration}
                  styles={styles}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            ...styles.getInputStyles(
              isFocused,
              input.trim().length > 0,
              theme,
              attachedSources.length > 0
            ),
            "& .MuiInputBase-input::placeholder": {
              color: theme.palette.primary.main,
              opacity: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatInput;
